import React from 'react';
import styled from 'styled-components';
import Title from '@/components/Title';
import { graphql } from 'gatsby';

import Container from '@/components/Container';
import { mediaQuery } from '@/utils/helpers';
import BlockWrapper from '@/components/BlockWrapper';
import { isDarkTheme } from '@/utils/StyleUtils';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import Paragraph from '@/components/Paragraph';
import { AboutType } from '@/components/About';
import Image, { ImageType } from '@/components/Image';

const ColumnsBlock = styled.ul`
  display: flex;
  padding: 0;
  list-style-type: none;
  flex-direction: column;
  gap: 2.4rem;

  > li {
    width: 100%;
  }

  ${mediaQuery.md(`
    flex-direction: row;
    margin-bottom: 8rem;
  `)}
`;

type ColumnType = {
  portrait: boolean;
};

const Column = styled.li<ColumnType>`
  ${mediaQuery.md(`
    position: relative;
    flex: 0 1 calc(1 / 3 * 100% - (1 - 1 / 3) * 2.4rem);

    &:first-child,
    &:last-child {
      top: 12rem;
    }

    ${({ portrait }) =>
      portrait &&
      `
      &:nth-child(1) {
        ul > li {
          height: 504px;
        }
      }

      &:nth-child(3) {
        ul > li {
          height: 504px;
        }
      }
    `}
  `)}
`;

const PicturesList = styled.ul`
  display: flex;
  padding: 0;
  list-style-type: none;
  flex-direction: column;
  gap: 2.4rem;
`;

const Picture = styled.li`
  ${mediaQuery.md(`
    height: 240px;

    > * {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  `)}
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding-top: 7.2rem;
`;

const BlockContent = styled.div`
  text-align: center;
  margin-bottom: 7rem;
`;

export type PicturesBlockType = {
  __typename?: string;
  id?: string;
  title: string;
  about: AboutType;
  subtitleMultipleLine: {
    subtitle: string;
  };
  background: {
    color: string;
  };
  pictures: Array<ImageType>;
  primaryLink: LinkType;
  secondaryLink: LinkType;
};

type Props = PicturesBlockType;
const PicturesBlock: React.FC<Props> = ({
  title,
  subtitleMultipleLine,
  background,
  pictures,
  primaryLink,
  secondaryLink,
}) => {
  const backgroundColor: string = background?.color;

  //divide pictures in 3 arrays
  const columns = new Array<Array<ImageType>>();
  let colNr = 0;
  for (let picNr = 0; picNr < pictures.length; picNr++) {
    if (!columns[colNr]) columns[colNr] = new Array<ImageType>();
    columns[colNr].push(pictures[picNr]);
    if (picNr !== 4) colNr++;
    if (colNr > 2) colNr = 0;
  }
  return (
    <BlockWrapper color={backgroundColor}>
      <Container>
        <BlockContent>
          <Title
            size='xl'
            level='h2'
            margin=' 2rem 0'
            lightColor={isDarkTheme(backgroundColor)}
          >
            {title}
          </Title>
          {subtitleMultipleLine && (
            <Paragraph
              color={isDarkTheme(backgroundColor) ? 'White' : 'GreyBlue'}
              weight='semiBold'
            >
              {subtitleMultipleLine.subtitle}
            </Paragraph>
          )}
        </BlockContent>
        {pictures && (
          <ColumnsBlock>
            {React.Children.toArray(
              columns.map((pictures, index) => (
                <Column key={index} portrait={pictures.length === 1}>
                  <PicturesList>
                    {React.Children.toArray(
                      pictures.map((picture) => {
                        return (
                          <Picture key={picture.file.url}>
                            <Image {...picture} />
                          </Picture>
                        );
                      }),
                    )}
                  </PicturesList>
                </Column>
              )),
            )}
          </ColumnsBlock>
        )}

        <CTAWrapper>
          <Link {...primaryLink} linkStyle={LinkStyle.PRIMARY} />
          <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
        </CTAWrapper>
      </Container>
    </BlockWrapper>
  );
};

export default PicturesBlock;
export const picturesBlockFragmentQuery = graphql`
  fragment PicturesBlockFragment on ContentfulPicturesBlock {
    id
    __typename
    title
    subtitleMultipleLine: subtitle {
      subtitle
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }

    background {
      color
    }
    pictures {
      gatsbyImageData(width: 2048)
      ...ImageFragment
    }
  }
`;
